import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Base from "../../components/BaseTemplate";
import { Button, Card, Row, Col, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "react-medium-image-zoom/dist/styles.css";
import ReactionSVG from "../../components/Reaction";
import { tError } from "../../components/customToast";
import useAPI from "../../components/utils/useAPI";
import { BackButton } from "../../components/BackButton";
import { SMARTSPlusImg } from "../../components/SMARTSPlusImg";
import Zoom from "react-medium-image-zoom";

export default function ReactionDetail() {
  // Hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall, error } = useAPI();

  // States
  const [rxn, setRxn] = useState({});

  // API Calls
  const getReactionDetail = async () => {
    try {
      const data = await apiCall({
        method: "GET",
        url: `/reactionClass/reaction/${id}`,
      });

      setRxn({
        ...data,
        smarts_description: data.smarts_description.split("\n"),
      });
    } catch (error) {
      tError(
        `Error: ${error.name}\n Message: ${error.message}\n Code: ${error.code}`
      );
    }
  };

  const SynthonCards = () => {
    return (
      <Row>
        {rxn.synthons?.map((syn, index) => {
          return (
            <Col md={4} key={`synthon-card-${index}`} className="mb-3">
              <Card
                border="0"
                className="mt-4 mb-4 shadow rounded-4 centerText h-100"
              >
                <Card.Header>{syn.synthon_name}</Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Synthon ID:</strong> {syn.synthon_id}
                  </div>

                  {syn.synthon_inclusion_smarts &&
                    syn.synthon_inclusion_smarts.length > 0 && (
                      <div className="mb-3">
                        <strong>Inclusion SMARTS:</strong>
                        <div className="mt-2">
                          {syn.synthon_inclusion_smarts.map(
                            (smart, smartIndex) => (
                              <div
                                key={`inclusion-${smartIndex}`}
                                className="small text-muted mb-1"
                              >
                                {smart}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {syn.synthon_exclusion_smarts &&
                    syn.synthon_exclusion_smarts.length > 0 && (
                      <div className="mb-3">
                        <strong>Exclusion SMARTS:</strong>
                        <div className="mt-2">
                          {syn.synthon_exclusion_smarts.map(
                            (smart, smartIndex) => (
                              <div
                                key={`exclusion-${smartIndex}`}
                                className="small text-muted mb-1"
                              >
                                {smart}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </Card.Body>
                <Zoom>
                  <Card.Img
                    className="rounded-4"
                    src={`/img/synthons/SYN-${syn.synthon_id}.png`}
                    alt={`${syn.synthon_id}.png`}
                  ></Card.Img>
                </Zoom>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  useEffect(() => {
    getReactionDetail(id);
  }, []);

  // Handle error display
  if (error) {
    return (
      <Base>
        <div className="alert alert-danger" role="alert">
          Error loading data: {error}
        </div>
      </Base>
    );
  }

  return (
    <Base>
      <Row>
        <Col>
          <BackButton path={"/reactions"} />
        </Col>
        <Col className="rightText">
          <Button
            href={
              "https://tldr.docking.org/start/enumeration?rxn_id=" + rxn.rxn_id
            }
            target="_blank"
            className="text-white fw-bold t"
          >
            Enumerate on TLDR
          </Button>
        </Col>
      </Row>

      {/* Reaction Detail */}
      <Card border="0" className="mb-4 shadow rounded-4 h-100">
        <Card.Header className="bg-gradient-primary py-3">
          <Row className="align-items-center">
            <Col xs="auto"></Col>
            <Col className="text-center">
              <h1 className="mb-0 fw-bold">{rxn.rxn_name}</h1>
              <Badge bg="light" text="dark" className="mt-2">
                ID: {rxn.rxn_id}
              </Badge>
            </Col>
            <Col xs="auto"></Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-4">
          <Row className="mb-4">
            <Col md={6}>
              <div className="mb-4">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-tag me-2"></i>Description
                </h5>
                <div className="bg-light p-3 rounded-3">
                  <p className="mb-0">{rxn.description || "Not available"}</p>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-4">
                <h5 className="fw-bold  mb-3">
                  <i className="fas fa-code me-2"></i>SMARTS
                </h5>
                <div className="bg-light p-3 rounded-3">
                  <code className="text-dark">
                    {rxn.smarts || "Not available"}
                  </code>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="mb-4">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-book me-2"></i>References
                </h5>
                <div className="bg-light p-3 rounded-3">
                  {rxn.smarts_description?.length > 0 ? (
                    <ul className="list-unstyled mb-0">
                      {rxn.smarts_description.map((description, index) => (
                        <li key={`description-${index}`} className="mb-2">
                          {description.slice(0, 4).toLowerCase() === "http" ? (
                            <a
                              href={description}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              {description}
                            </a>
                          ) : (
                            <span>{description}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-muted fst-italic">
                      No references available
                    </span>
                  )}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-4">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-tags me-2"></i>Tags
                </h5>
                <div className="bg-light p-3 rounded-3">
                  {rxn.tags?.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {rxn.tags.map((tag, index) => (
                        <Badge
                          key={`tag-${index}`}
                          bg="secondary"
                          className="px-3 py-2"
                        >
                          {tag.label || tag}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted fst-italic">
                      No tags assigned
                    </span>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Reaction Details and SMARTS Plus Image  */}

      {/* RDKit SVG */}
      <Row>
        <Col>
          <Card border="0" className="mb-4 shadow rounded-4 h-100">
            {/* <h5 className="text-center mt-5">{rxn.smarts}</h5> */}
            <Card.Body className="d-flex align-items-center justify-content-center h-100 p-0">
              <div
                style={{
                  width: "100%",
                  height: "225px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReactionSVG rxnSmarts={String(rxn.smarts)} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* SMARTSPlus Image */}
        <Col>
          <Card
            border="0"
            className="mb-4 shadow rounded-4 align-items-center h-100"
          >
            <SMARTSPlusImg id={id} />
          </Card>
        </Col>
      </Row>

      {/* Synthons */}
      <SynthonCards />
    </Base>
  );
}
